// Zadanie zamkniete E8 P/F: pierwiastki i porownywanie wartosci wyrazen.

import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function reducedFraction(num, den) {
  const divisor = gcd(num, den);
  return { num: num / divisor, den: den / divisor };
}

function fractionLatex({ num, den }) {
  if (den === 1) return String(num);
  if (num < 0) return `-\\frac{${Math.abs(num)}}{${den}}`;
  return `\\frac{${num}}{${den}}`;
}

function buildData(p, q, firstClaim, secondClaim) {
  const k = reducedFraction(p - q, p * p * q * q);
  const l = p * q * (q - p);
  const firstTrue = firstClaim === "negative" ? k.num < 0 : k.num > 0;
  const secondTrue = secondClaim === "greater" ? l > k.num / k.den : l < k.num / k.den;

  return {
    p,
    q,
    k,
    l,
    firstClaim,
    secondClaim,
    correctPF: [firstTrue ? "P" : "F", secondTrue ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8RootExpressionsTruthClosedTask",
    data: buildData(4, 3, "negative", "greater"),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 7,
    },
    tags: ["e8", "potegi", "pierwiastki", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const p = pick([2, 3, 4, 5, 6]);
  const q = pick([2, 3, 4, 5, 6].filter((value) => value !== p));

  return {
    type: "E8RootExpressionsTruthClosedTask",
    data: buildData(
      p,
      q,
      pick(["negative", "positive"]),
      pick(["greater", "less"])
    ),
    source: { variant: "random", basedOn: "E8_2025_MAJ_7" },
    tags: ["e8", "potegi", "pierwiastki", "prawda_falsz"],
  };
}

export function renderTask({ data }) {
  const { p, q, firstClaim, secondClaim } = data;
  const kLatex = `K=\\frac{1}{${q * q}}\\cdot\\sqrt{\\frac{1}{${p * p}}}-\\frac{1}{${p * p}}\\cdot\\sqrt{\\frac{1}{${q * q}}}`;
  const lLatex = `L=${q * q}\\sqrt{${p * p}}-${p * p}\\sqrt{${q * q}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Dane są wyrażenia:</p>
      <BlockMath math={`${kLatex},\\qquad ${lLatex}.`} />
      <p>
        Oceń prawdziwość podanych stwierdzeń. Wybierz <InlineMath math="P" />,
        jeśli stwierdzenie jest prawdziwe, albo <InlineMath math="F" />, jeśli
        jest fałszywe.
      </p>
      <p>
        1. Liczba <InlineMath math="K" /> jest{" "}
        {firstClaim === "negative" ? "ujemna" : "dodatnia"}.
      </p>
      <p>
        2. Liczba <InlineMath math="L" /> jest{" "}
        {secondClaim === "greater" ? "większa" : "mniejsza"} od liczby{" "}
        <InlineMath math="K" />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) =>
    onChange?.({ ...value, [key]: nextValue });

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput
            value={value[key] || null}
            disabled={disabled}
            onChange={(nextValue) => setValue(key, nextValue)}
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    userAnswer?.s1 === data.correctPF[0] &&
    userAnswer?.s2 === data.correctPF[1]
  );
}

export function renderAnswer({ data }) {
  return (
    <InlineMath
      math={`1.\\ \\text{${data.correctPF[0]}},\\qquad 2.\\ \\text{${data.correctPF[1]}}`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath
      math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\qquad 2.\\ \\text{${userAnswer?.s2 || "-"}}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { p, q, k, l, firstClaim, secondClaim, correctPF } = data;
  const kSign = k.num < 0 ? "ujemna" : "dodatnia";
  const relation = l > k.num / k.den ? ">" : "<";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Obliczamy pierwiastki z ułamków. Pierwiastek
        liczymy osobno z licznika i mianownika.
      </p>
      <BlockMath
        math={`\\sqrt{\\frac{1}{${p * p}}}=\\frac{1}{${p}},\\qquad \\sqrt{\\frac{1}{${q * q}}}=\\frac{1}{${q}}`}
      />
      <p>
        <strong>Krok 2.</strong> Podstawiamy te wartości do wyrażenia{" "}
        <InlineMath math="K" /> i sprowadzamy ułamki do wspólnego mianownika.
      </p>
      <BlockMath
        math={`K=\\frac{1}{${q * q}}\\cdot\\frac{1}{${p}}-\\frac{1}{${p * p}}\\cdot\\frac{1}{${q}}=\\frac{${p}}{${p * p * q * q}}-\\frac{${q}}{${p * p * q * q}}=${fractionLatex(k)}`}
      />
      <p>
        Zatem liczba <InlineMath math="K" /> jest <strong>{kSign}</strong>.
        Pierwsze stwierdzenie jest{" "}
        <strong>{correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 3.</strong> Obliczamy pierwiastki w wyrażeniu{" "}
        <InlineMath math="L" />.
      </p>
      <BlockMath
        math={`L=${q * q}\\cdot${p}-${p * p}\\cdot${q}=${q * q * p}-${p * p * q}=${l}`}
      />
      <p>
        <strong>Krok 4.</strong> Porównujemy otrzymane liczby.
      </p>
      <BlockMath math={`L=${l}\\ ${relation}\\ ${fractionLatex(k)}=K`} />
      <p>
        Drugie stwierdzenie, że <InlineMath math="L" /> jest{" "}
        {secondClaim === "greater" ? "większa" : "mniejsza"} od{" "}
        <InlineMath math="K" />, jest{" "}
        <strong>{correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
      <p>
        <strong>Krok 5.</strong> Zaznaczamy odpowiedzi{" "}
        <InlineMath
          math={`1.\\ \\text{${correctPF[0]}}\\quad\\text{i}\\quad 2.\\ \\text{${correctPF[1]}}`}
        />
        .
      </p>
    </div>
  );
}

